import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DropdownOptions } from 'app/core/components/dropdown-selection/dropdown-selection.component';
import { SchoolActivity, SchoolActivityInstance, SchoolActivityState } from 'app/models';
import { FeatureFlagService, FLAGS } from 'app/services/feature-flag.service';
import { FlexPeriod, FlexPeriodService, FlexVisibility } from 'app/services/flex-period.service';
import { InstanceTimes } from 'app/services/school-activity.service';
import { SelectDaysComponent } from 'app/shared/shared-components/select-days/select-days.component';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import {
	FLEX_INSTANCE_TIMES_KEY,
	FLEX_PERIOD_ID_KEY,
	FLEX_PERIOD_REPEATS_KEY,
	SCHEDULE_REPEAT_END_DATE_KEY,
	VISIBILITY_GROUP_IDS_KEY,
	VISIBILITY_KEY,
	VISIBILITY_STUDENT_IDS_KEY,
} from '../activity-form-util';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sp-activity-schedule-form-flex',
	providers: [DatePipe],
	template: `
		<div class="tw-flex tw-flex-col tw-gap-8 tw-pb-3" *ngIf="this.editScheduleForm.get('flexPeriodId')! | formValue | async as flexPeriodIdControl">
			<div class="tw-flex tw-flex-col tw-gap-3" *ngIf="flexPeriodService.flexPeriods$ | async as flexPeriods">
				<sp-select
					label="Flex Period"
					placeholder="Select flex period"
					[options]="getFlexPeriodOptions(flexPeriods)"
					[selectedOption]="getSelectedFlexPeriodOptions(flexPeriodIdControl.value, flexPeriods)"
					(selected)="handleFlexPeriodSelection($event)"></sp-select>

				<!-- BEGIN flex period schedule display -->
				<ng-container *ngIf="flexPeriodService.flexPeriod$(flexPeriodIdControl.value) | async as flexPeriod">
					<div
						*ngFor="let schedule of flexPeriod.schedules"
						class="tw-flex tw-gap-2 tw-bg-gray-050 tw-border tw-border-solid tw-border-gray-150 tw-p-3 tw-rounded-lg">
						<bp-icon icon="clock-01" class="tw-text-navy-500"></bp-icon>
						<div class="tw-flex tw-flex-col tw-gap-1">
							<div class="tw-text-navy-500 tw-font-semibold tw-text-sm">
								{{ schedule.start_hour + ':' + schedule.start_minute | militaryTimeString }} -
								{{ schedule.end_hour + ':' + schedule.end_minute | militaryTimeString }}
							</div>
							<div class="tw-text-gray-500 tw-text-xs">{{ flexPeriodService.DisplayDaysString(schedule.days_of_week) }}</div>
						</div>
					</div>
				</ng-container>
				<!-- END flex period schedule display -->
			</div>

			<ng-container *ngIf="flexPeriodIdControl.value">
				<div class="tw-flex tw-flex-col tw-gap-3">
					<sp-select
						*ngIf="flexPeriodRepeatsOptions$ | async as options"
						label="Repeats"
						placeholder="Select repeat options"
						[options]="options"
						[selectedOption]="selectedFlexPeriodRepeatsOption || null"
						(selected)="handleFlexPeriodRepeatsSelection($event)"></sp-select>
					<ng-container *ngIf="customScheduleEnabled && flexPeriodRepeatsControl?.value == 'flex_recurring'">
						<sp-calendar-input [control]="repeatEndControl" [setCallback]="endDateSelected.bind(this)"></sp-calendar-input>
					</ng-container>
				</div>
			</ng-container>

			<div class="ds-flex-row tw-relative" *ngIf="flexPeriodRepeatsControl?.value == 'scheduled'" (click)="openSelectDaysModal('scheduled')">
				<div class="tw-text-navy-500 tw-text-base tw-font-semibold tw-mb-3">Dates</div>
				<div
					class="tw-w-full tw-flex tw-items-center tw-text-navy-500 tw-font-normal tw-text-base tw-p-3 tw-rounded-lg tw-border tw-border-gray-150 tw-border-solid tw-box-border tw-cursor-pointer tw-bg-gray-050 tw-truncate">
					{{ dateDisplayString$ | async }}
				</div>
			</div>
		</div>
	`,
})
export class ActivityScheduleFlexFormComponent implements OnInit, OnDestroy {
	@ViewChild('repeatEndField') repeatEndField!: ElementRef;

	@Input() editScheduleForm!: UntypedFormGroup;
	@Input() activity: SchoolActivity | null = null;
	@Input() flexPeriodId: number | null = null;
	@Input() activityInstances!: BehaviorSubject<SchoolActivityInstance[]> | null;

	flexPeriodRepeatsControl!: UntypedFormControl;
	repeatEndControl!: UntypedFormControl;
	visibilityControl!: UntypedFormControl;

	flexPeriodRepeatsOptions$!: Observable<DropdownOptions<SchoolActivityState>[]>;
	dateDisplayString$!: Observable<string>;

	selectedFlexPeriodRepeatsOption: DropdownOptions<SchoolActivityState> | undefined;

	customScheduleEnabled = false;
	destroy$ = new Subject<void>();

	constructor(
		private datePipe: DatePipe,
		private dialog: MatDialog,
		private featureService: FeatureFlagService,
		public flexPeriodService: FlexPeriodService
	) {}

	ngOnInit() {
		this.customScheduleEnabled = this.featureService.isFeatureEnabledV2(FLAGS.ActivityManagement);

		// Live updates based on form values:
		this.flexPeriodRepeatsControl = this.editScheduleForm.get(FLEX_PERIOD_REPEATS_KEY) as UntypedFormControl;
		this.repeatEndControl = this.editScheduleForm.get(SCHEDULE_REPEAT_END_DATE_KEY) as UntypedFormControl;
		this.visibilityControl = this.editScheduleForm.get(VISIBILITY_KEY) as UntypedFormControl;

		const formValueChangesWithInitialValue = this.editScheduleForm.valueChanges.pipe(startWith(this.editScheduleForm.value));

		// Flex period repeat options always include the selected flex period's name
		this.flexPeriodRepeatsOptions$ = combineLatest([this.flexPeriodService.flexPeriods$, formValueChangesWithInitialValue]).pipe(
			switchMap(([flexPeriods, formValue]) => {
				const selectedFlexPeriod = flexPeriods.find((fp) => fp.id === formValue[FLEX_PERIOD_ID_KEY]);
				const dropdownOptions: DropdownOptions<SchoolActivityState>[] = [
					{ title: `Every ${selectedFlexPeriod?.name}`, value: 'flex_recurring' },
					{ title: 'On Selected Days', value: 'scheduled' },
				];
				return [dropdownOptions];
			})
		);

		this.flexPeriodRepeatsOptions$.pipe(takeUntil(this.destroy$)).subscribe((options) => {
			this.selectedFlexPeriodRepeatsOption = options.find((opt) => opt.value === this.editScheduleForm.get([FLEX_PERIOD_REPEATS_KEY])?.value);
		});

		this.dateDisplayString$ = formValueChangesWithInitialValue.pipe(
			takeUntil(this.destroy$),
			map((formValue) => {
				const newDates: Date[] = (formValue[FLEX_INSTANCE_TIMES_KEY] ?? []).map((newInstance: InstanceTimes) => newInstance.start);

				let allDates: Date[];
				if (this.activity?.state == formValue[FLEX_PERIOD_REPEATS_KEY]) {
					const existingDates =
						this.flexPeriodId == formValue[FLEX_PERIOD_ID_KEY]
							? this.activityInstances?.value.map((instance) => new Date(instance.start_time)) ?? []
							: [];

					allDates = existingDates.concat(newDates).sort((a, b) => a.getTime() - b.getTime());
				} else {
					allDates = newDates.sort((a, b) => a.getTime() - b.getTime());
				}

				if (allDates.length > 0) {
					return allDates.map((d) => this.datePipe.transform(d, 'MMM d')).join(', ');
				} else {
					return 'Select dates';
				}
			})
		);
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	getFlexPeriodOptions(flexPeriods: FlexPeriod[] | null): DropdownOptions<FlexPeriod>[] {
		if (!flexPeriods) {
			return [];
		}

		return flexPeriods.map((flexPeriod) => ({ title: flexPeriod.name, value: flexPeriod }));
	}

	getSelectedFlexPeriodOptions(flexPeriodId: number | null, flexPeriods: FlexPeriod[] | null) {
		if (!flexPeriods) {
			return null;
		}

		const options = this.getFlexPeriodOptions(flexPeriods);
		const selected = flexPeriods.find((fp) => fp.id === flexPeriodId);
		return options.find((opt) => opt.value.id === selected?.id) ?? null;
	}

	handleFlexPeriodSelection(selected: DropdownOptions<FlexPeriod>): void {
		this.editScheduleForm.patchValue({ [FLEX_PERIOD_ID_KEY]: selected.value.id });
		this.flexPeriodId = selected.value.id;
		let visibility = FlexVisibility.Visible;
		if (selected.value.visibility === FlexVisibility.Restricted) {
			visibility = FlexVisibility.Restricted;
		}
		this.editScheduleForm.get([VISIBILITY_KEY])?.patchValue(visibility);
		this.editScheduleForm.get([VISIBILITY_STUDENT_IDS_KEY])?.patchValue([]);
		this.editScheduleForm.get([VISIBILITY_GROUP_IDS_KEY])?.patchValue([]);
	}

	protected handleFlexPeriodRepeatsSelection(selected: DropdownOptions<SchoolActivityState>): void {
		const originalValue = this.editScheduleForm.value[FLEX_PERIOD_REPEATS_KEY];
		this.editScheduleForm.patchValue({ [FLEX_PERIOD_REPEATS_KEY]: selected.value });

		// If the activity was recurring, and option selected is scheduled, we want
		// to open the select days modal, and display the date field with the
		// instances already created displayed
		if (originalValue === 'flex_recurring' && selected.value === 'scheduled') {
			void this.openSelectDaysModal(selected.value);
		}
	}

	async openSelectDaysModal(state: SchoolActivityState): Promise<void> {
		const preselected = ((this.editScheduleForm.get(FLEX_INSTANCE_TIMES_KEY)?.value ?? []) as InstanceTimes[])
			.filter((instanceTime: InstanceTimes) => {
				return !this.activityInstances?.value.find((instance) => moment(instanceTime.start).isSame(moment(instance.start_time), 'minute'));
			})
			.map((instanceTime: InstanceTimes) => instanceTime.start);

		let instances: SchoolActivityInstance[] = this.activityInstances?.value ?? [];
		if (this.activity?.state === 'flex_recurring') {
			instances = [];
		}

		const selectDialog = this.dialog.open(SelectDaysComponent, {
			panelClass: 'select-days-flex',
			width: '433px',
			disableClose: true,
			data: {
				flexPeriod: await firstValueFrom(this.flexPeriodService.flexPeriod$(this.editScheduleForm.get(FLEX_PERIOD_ID_KEY)?.value)),
				state: state,
				instances: instances,
				preSelectedDates: preselected,
			},
		});
		selectDialog.componentInstance.closeDialog = (state: SchoolActivityState, times: InstanceTimes[]) => {
			this.editScheduleForm.get(FLEX_PERIOD_REPEATS_KEY)?.setValue(state);
			selectDialog.close();

			if (state === 'flex_recurring') {
				this.editScheduleForm.get(FLEX_INSTANCE_TIMES_KEY)?.setValue([]);
				return;
			}

			const newTimes: InstanceTimes[] = this.editScheduleForm.get(FLEX_INSTANCE_TIMES_KEY)?.value || [];
			const instanceTimes = times.reduce((acc, time) => {
				if (!newTimes.find((t) => t.start === time.start)) {
					acc.push(time);
				}
				return acc;
			}, [] as InstanceTimes[]);

			this.editScheduleForm.get(FLEX_INSTANCE_TIMES_KEY)?.setValue(instanceTimes);
		};
	}

	endDateSelected($event: moment.Moment[]): void {
		const endDate = $event[0].endOf('day');
		this.editScheduleForm.patchValue({ [SCHEDULE_REPEAT_END_DATE_KEY]: endDate });
	}
}
